import * as mobx from "mobx";
import { makeObservable } from "mobx";
import {action, observable} from 'mobx'
import LoginStore from "../session/LoginStore";
import * as ConfigApi from "./ConfigApi"
import {stallUnresolvedPromises} from "../../util/async";


export class BrandConfigStore {

  /**
   * the brandConfig. See chatmeter/domain/src/main/scala/com/chatmeter/domain/config/BrandConfig.scala
   *
   * typedef for intellij autocomplete
   *
   * @typedef {Object} BrandConfig
   * @property {string} name
   * @property {string} logoUrl
   * @property {string} darkThemeLogoUrl
   * @property {string} hostname
   * @property {string} faviconUrl
   * @property {string} logoutRedirectUrl
   * @property {string} reportUrl
   * @property {string} defaultPrimaryCategory?
   * @property {Array.<{isDefault: boolean, languageCode: string}>} localeInfo
   * @property {*} reviewTags
   * @property {*} reviewBuilderProviderName
   * @property {*} samlUrl
   * @property {*} oktaUrl
   * @property {*} ssoButtonOnly
   * @property {*} termsOfService
   * @property {*} passwordReset
   * @property {*} registrationUrl
   * @property {*} authErrorMessage
   * @property {string} rebrandClass?
   * @property {*} mapRange
   * @property {{
   *    hideAccount: boolean,
   *    hideLogout: boolean,
   *    hideHelp: boolean,
   *    hideHelpCenter: boolean,
   *    hideSnapshot: boolean,
   *    hideListings: boolean,
   *    hideReviews: boolean,
   *    hideRankings: boolean,
   *    hideScores: boolean,
   *    hideScores2: boolean,
   *    hideMedia: boolean,
   *    hideWorkflow: boolean,
   *    hideCompetitors: boolean,
   *    hideReports: boolean,
   *    hideLogo: boolean,
   *    hideWelcomeText: boolean,
   *    hideLogoutButtonOnSSO: boolean,
   *    hideAddLocationsForStandardUsers: boolean,
   *    hideEditLocationsForStandardUsers: boolean,
   *    hideAddAccount: boolean,
   *    hideUserSnap: boolean,
   *    hideReviewGeneration: boolean,
   *    hideUploadTool: boolean,
   *    hideSentiment: boolean,
   *    hideGoogleInsights: boolean,
   *    hideEmailAlertsAdmin: boolean,
   *    hideChatter2: boolean,
   *    hideChatterPublishing: boolean,
   *    hideReviewDetails: boolean,
   *    hideExternalResponseLink: boolean,
   *    hideReviewResponses: boolean,
   *    hideDemoReviewResponses: boolean,
   *    hideUnpublishedApartmentGuideReviews: boolean,
   *    hideApartmentGuideReviewResponses: boolean,
   *    hideGolfAdvisorReviewResponses: boolean,
   *    hideClientLocationId: boolean,
   *    hideListingManagementAccuracyReport: boolean,
   *    listingManagementOnly: boolean,
   *    hideYelpResponding: boolean,
   *    hideWorkflow2: boolean,
   *    hideInsights: boolean,
   *    hideLocalize: boolean,
   *    hideListingOptimizationReport: boolean,
   *    hideListingMissingReport: boolean,
   *    hideListingDuplicatesReport: boolean,
   *    hideReviewStoreComparison: boolean,
   *    hidePulseStoreComparison: boolean,
   *    hideScoresStoreComparison: boolean,
   *    hideSearchAnalyticsStoreComparison: boolean,
   *    hideSocialStoreComparison: boolean,
   *    hideReviewBulkResponding: boolean,
   *    condensedAccountLinks: boolean,
   *    hideNewUiOptIn: boolean,
   *    hideRestaurantMenus: boolean,
   *    hideReviewResponseGeneration: boolean
   * }} restrictions
   *
   *
   */
  /**
   *
   * @type {BrandConfig}
   */
  brandConfig = null;

  /**
   * host to fetch the brand config with
   */
  host = null;

  /**
   * whether the application was initialized with the iFrame=true flag in the url
   */
  isIframe = false;

  /**
   * true if the brand config was determined via the windows hostname instead of the actual user.
   * Will query the user's brand config once they are logged in
   * */
  isApproximate = true;


  constructor() {
    makeObservable(this, {
      brandConfig: observable,
      host: observable,
      isIframe: observable,
      isApproximate: observable,
      resetBrandConfig: action,
      setBrandConfig: action.bound,
      trySetHost: action,
      setIframeTrue: action,
      _setBrandConfigLogoUrl: action
    });

    // re-fetch the brand config whenever the conditions that determine the brand config change
    // some scenarios to keep in mind:
    // - logged in as a user, fetch their accounts brand config
    // - logging into a whitelabelled account (e.g. show their brand logo,
    //    or show their brand theme, but no user is available for a specific account. config is fetched via the hostname)
    // - logging into a sub-account of a whitelabelled account. For example a reseller has a login with one theme, but the user is
    //    of a sub-account with their own theme

    mobx.reaction(
      () => [LoginStore.attemptedAutomatedLogin, LoginStore.token, this.host],
      ([attempted, ...rest]) => attempted && this.resetBrandConfig(rest),
      {
        fireImmediately: true,
        equals: mobx.comparer.structural
      }
    )
  }

  resetBrandConfig = () => {
    if (LoginStore.renderOnly) {
      return;
    }
    if (LoginStore.token || this.host) {
      const wasApproximate = this.isApproximate
      this.isApproximate = !LoginStore.token
      if (wasApproximate && !this.isApproximate && this.brandConfig) {
        this.brandConfig = null
      }
      LoginStore.runUsingCachedResult({
        key: "BrandConfigStore.brandConfig",
        promise: this._fetchBrandConfig(),
        callback: this.setBrandConfig
      })
    } else {
      this.brandConfig = null
      this.isApproximate = true
    }
  };

 _fetchBrandConfig = stallUnresolvedPromises(() => {

   if (LoginStore.token) {
     return ConfigApi.getBrandConfig();
   } else if (this.host) {
     return ConfigApi.getBrandConfigFromHost(this.host);
   } else {
     return Promise.reject(
       new Error("no host or token set. Cannot determine brand config")
     );
   }
 })

 setBrandConfig(brandConfig) {
   this.brandConfig = brandConfig;
 };

  /**
   * sets the host if currently unset
   */
  trySetHost(hostname) {
    if (!this.host) {
      this.host = hostname
    }
  }

  setIframeTrue() {
    this.isIframe = true
  }

  updateLogo = async (file) => {
    const updatedLogo = await ConfigApi.saveLogo({logo: file });
    this._setBrandConfigLogoUrl(updatedLogo)
  }

  _setBrandConfigLogoUrl = (url) => {
    this.brandConfig.logoUrl = url
    this.brandConfig.darkThemeLogoUrl = ""
  };

}

export default new BrandConfigStore()
